import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import transcriptionService from '../services/transcriptionService';

// Maximum number of pending dictation segments to queue before dropping oldest (FIFO)
const MAX_PENDING_DICTATION_QUEUE = 50;

interface ClassificationState {
  category: string | null;
  pathologyType: string | null;
  confidence: number | null;
}

interface TranscriptionState {
  text: string;
  finalizedText: string;
  rawText: string;
  finalizedRawText: string;
  correctedText: string;
  finalizedCorrectedText: string;
  pendingCorrections: number;
  isEditMode: boolean;
  isFinal: boolean;
  isIncrementalCorrecting: boolean;
  isRefiningInFlight: boolean;
  sessionId: string | null;
  loading: boolean;
  error: string | null;
  classification: ClassificationState;
  // Append mode (continue recording)
  isAppendMode: boolean;
  // Corrected transcript editing
  isEditingCorrected: boolean;
  editedCorrectedText: string;
  isUserEdited: boolean;
  // Manual typing mode (type without dictation)
  isManualTypingMode: boolean;
  manualTypedText: string;
  // Tracks cursor position during simultaneous typing+dictation to prevent data loss
  cursorPosition: number;
  // Tracks the most recently dictated segment appended during simultaneous typing+dictation.
  // Used by TranscriptPanel to briefly highlight new dictated content.
  latestDictationDelta: string;
  // Queue of pending dictation segments that arrived while cursor was not at end.
  // Processed when cursor moves to end to ensure no data loss.
  pendingDictationQueue: string[];
  // ── Real-time patch mode state (Phase 4-5) ───────────────────────
  lastUserEditAt: number;          // Timestamp of last manual edit
  deferredPatches: number;        // Count of patches skipped due to user edit
  appliedPatchIds: string[];       // Dedup set of applied patch IDs
  lastCorrectionFailed: boolean;  // True when backend LLM correction fell back to raw
  // When enabled, suppresses real-time incremental corrections. Raw STT streams
  // directly into Reviewed Dictation; a single LLM correction runs on stop.
  deferredCorrectionEnabled: boolean;
  history: Array<{
    id: number;
    text: string;
    createdAt: string;
    status?: string;
    sessionId?: string;
  }>;
}

const initialState: TranscriptionState = {
  text: '',
  finalizedText: '',
  rawText: '',
  finalizedRawText: '',
  correctedText: '',
  finalizedCorrectedText: '',
  pendingCorrections: 0,
  isEditMode: false,
  isFinal: false,
  isIncrementalCorrecting: false,
  isRefiningInFlight: false,
  sessionId: null,
  loading: false,
  error: null,
  classification: {
    category: null,
    pathologyType: null,
    confidence: null,
  },
  isAppendMode: false,
  isEditingCorrected: false,
  editedCorrectedText: '',
  isUserEdited: false,
  isManualTypingMode: false,
  manualTypedText: '',
  cursorPosition: 0,
  latestDictationDelta: '',
  pendingDictationQueue: [],
  lastUserEditAt: 0,
  deferredPatches: 0,
  appliedPatchIds: [],
  lastCorrectionFailed: false,
  deferredCorrectionEnabled: false,
  history: [],
};

export const saveTranscription = createAsyncThunk(
  'transcription/save',
  async ({ text, sessionId, cptCode, cptExamName, correctedText, cptLaterality, cptContrast }: { text: string; sessionId: string | null; cptCode?: string | null; cptExamName?: string | null; correctedText?: string | null; cptLaterality?: string | null; cptContrast?: string | null }, { rejectWithValue }) => {
    try {
      const response = await transcriptionService.saveTranscription(text, sessionId, cptCode, cptExamName, correctedText, cptLaterality, cptContrast);
      return response;
    } catch (error: unknown) {
      const msg = (error as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Failed to save transcription';
      return rejectWithValue(msg);
    }
  }
);

export const fetchTranscriptionHistory = createAsyncThunk(
  'transcription/fetchHistory',
  async (_, { rejectWithValue }) => {
    try {
      const response = await transcriptionService.getTranscriptionHistory();
      return response;
    } catch (error: unknown) {
      const msg = (error as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Failed to fetch transcription history';
      return rejectWithValue(msg);
    }
  }
);

const transcriptionSlice = createSlice({
  name: 'transcription',
  initialState,
  reducers: {
    addTranscriptionText: (state, action: PayloadAction<{ text: string; isFinal: boolean }>) => {
      if (action.payload.isFinal) {
        state.finalizedText = (state.finalizedText + ' ' + action.payload.text).trim();
        state.text = state.finalizedText;
      } else {
        state.text = (state.finalizedText + ' ' + action.payload.text).trim();
      }
      // NOTE: Do NOT set state.isFinal from per-turn raw payloads. ``isFinal`` is
      // a session-level flag ("dictation has stopped, no more audio coming") and
      // is owned by ``setIsFinal`` (dispatched from finalizeStop) and
      // ``loadSessionForAppend``. Letting per-turn end_of_turn flicker isFinal
      // mid-recording corrupted downstream guards (e.g. addCorrectedTranscript
      // dropping the final-pass correction when isFinal happened to be true at
      // that moment) and broke the transcriptFinalized pipeline readiness check.
      //
      // The `isFinal` field in the action payload is INTENTIONALLY IGNORED —
      // it represents per-turn STT finality (end_of_turn), not session finality.
    },
    addRawTranscript: (state, action: PayloadAction<{ text: string; isFinal: boolean }>) => {
      if (action.payload.isFinal) {
        state.finalizedRawText = (state.finalizedRawText + ' ' + action.payload.text).trim();
        state.rawText = state.finalizedRawText;
        // A final raw segment means LLM correction is now in-flight
        state.pendingCorrections += 1;
      } else {
        state.rawText = (state.finalizedRawText + ' ' + action.payload.text).trim();
      }
    },
    // Deferred mode: mirror raw transcript directly to correctedText.
    // Accumulates ONLY on final updates:
    // - Final: append to finalizedCorrectedText, show full accumulated text
    // - Non-final: show interim (AssemblyAI sends progressively longer text for the same turn)
    addDeferredRawToCorrected: (state, action: PayloadAction<{ text: string; isFinal: boolean }>) => {
      const incoming = action.payload.text.trim();
      const priorFinal = state.finalizedCorrectedText || '';

      // Helper: check if incoming already contains or extends priorFinal to avoid duplication
      const isExtension = priorFinal && (
        incoming.startsWith(priorFinal) ||
        incoming.includes(priorFinal.slice(-50)) // last 50 chars overlap
      );

      if (action.payload.isFinal) {
        // Final turn - permanently append to accumulated text (avoiding duplication)
        const newFinal = isExtension ? incoming : (priorFinal ? (priorFinal + ' ' + incoming).trim() : incoming);
        state.finalizedCorrectedText = newFinal;
        state.correctedText = newFinal;
      } else {
        // Non-final turn - show interim without modifying finalized
        // AssemblyAI sends progressively longer text that may already include prior content
        const display = isExtension ? incoming : (priorFinal ? (priorFinal + ' ' + incoming).trim() : incoming);
        state.correctedText = display;
      }
    },
    // Replace the raw transcript with the backend's authoritative snapshot.
    // Used on the final transcript_update (is_final=true with corrected text) so the
    // DICTATION CAPTURE shown to the user matches the snapshot the LLM corrected,
    // discarding any trailing partial that was never finalised by the STT provider.
    //
    // Always assigns the payload — including empty strings — so that an authoritative
    // empty snapshot (silence with a stuck partial) correctly clears the stale partial
    // from rawText/finalizedRawText. Callers in AudioRecorder.tsx already gate the
    // dispatch on `data.is_final && data.raw`; this reducer is the source of truth for
    // reconciliation and must not second-guess that contract.
    setFinalRawText: (state, action: PayloadAction<string>) => {
      const text = (action.payload ?? '').trim();
      // Debug warning: empty final raw text may indicate backend issue
      if (!text && action.payload !== '') {
        console.warn('[transcriptionSlice] setFinalRawText received null/undefined, defaulting to empty string');
      }
      // Only reconcile raw fields — formatted text fields (text/finalizedText)
      // are populated by addTranscriptionText with capitalised/punctuated output
      // and should not be clobbered with the unformatted snapshot.
      state.finalizedRawText = text;
      state.rawText = text;
    },
    // DEPRECATED: Part of the legacy real-time patch pipeline (ENABLE_CORRECTION_PATCH).
    // Never dispatched in the default deferred-correction flow.
    // Retained for legacy testing when ENABLE_DEFERRED_CORRECTION is explicitly disabled.
    applyCorrectionPatch: (state, action: PayloadAction<{
      start_index: number;
      end_index: number;
      corrected_text: string;
      confidence: number;
      chunk_id: string;
      raw_chunk: string;
      triggered_at: number;
    }>) => {
      const payload = action.payload;
      // Skip duplicate patch IDs
      if (state.appliedPatchIds.includes(payload.chunk_id)) {
        return;
      }
      // Skip if user edited after this correction was triggered
      if (state.lastUserEditAt > 0 && payload.triggered_at > 0 && state.lastUserEditAt > payload.triggered_at) {
        state.deferredPatches += 1;
        return;
      }
      // Apply patch via string splice
      const current = state.correctedText || '';
      const start = Math.max(0, Math.min(payload.start_index, current.length));
      const end = Math.max(start, Math.min(payload.end_index, current.length));
      state.correctedText = current.slice(0, start) + payload.corrected_text + current.slice(end);
      // Do NOT sync finalizedCorrectedText here. Patches are incremental updates;
      // finalizedCorrectedText should only capture the definitive final-pass state.
      // Syncing it caused startsWith() failures in addCorrectedTranscript when the
      // LLM reformatted text between triggers, producing duplication.
      state.appliedPatchIds.push(payload.chunk_id);
      if (state.appliedPatchIds.length > 100) {
        state.appliedPatchIds.shift(); // Prevent unbounded growth
      }
    },
    acceptDeferredPatches: (state) => {
      state.lastUserEditAt = 0;
      state.deferredPatches = 0;
      // A full re-correction will be triggered by the backend on next interval
    },
    setCorrectionFailed: (state, action: PayloadAction<boolean>) => {
      state.lastCorrectionFailed = action.payload;
    },
    addCorrectedTranscript: (state, action: PayloadAction<{ raw: string; corrected: string; isIncremental?: boolean; isFinal?: boolean; fallback?: boolean }>) => {
      // NOTE: An earlier ``isFinal && isAppendMode`` early-return guard was
      // removed here. It was intended to drop stale corrections in loaded-only
      // viewing mode, but in practice loaded-only mode has no live WS so the
      // guard never had a real threat to defend against. Worse, when other
      // reducers (notably the prior ``addTranscriptionText`` behaviour) flipped
      // ``isFinal`` true on each end_of_turn raw, the guard could spuriously
      // drop the legitimate final-pass corrected transcript — leaving
      // ``correctedText`` stuck on the most recent incremental output.

      // ── Capture pre-mutation state for simultaneous dictation merge ─────────
      // Must be captured BEFORE state.correctedText is updated below, otherwise
      // the delta computation would compare the new value against itself (always empty).
      const prevCorrectedSnapshot = (state.correctedText || '').trim();

      // In deferred mode, backend sends single-turn text (not cumulative).
      // Mirror addRawTranscript logic: non-final updates are temporary, final updates append.
      let combined: string;
      if (state.deferredCorrectionEnabled && action.payload.isIncremental) {
        // Deferred mode: treat like addRawTranscript
        // isFinal=false: show interim result (prior + current, but don't save)
        // isFinal=true: append to accumulated finalizedCorrectedText
        const incoming = (action.payload.corrected || '').trim();
        const isFinal = action.payload.isFinal ?? false;
        const priorFinal = (state.finalizedCorrectedText || '').trim();
        if (isFinal) {
          // Final turn - append to accumulated text (like addRawTranscript does for finalizedRawText)
          combined = priorFinal ? `${priorFinal} ${incoming}`.trim() : incoming;
          state.finalizedCorrectedText = combined;
        } else {
          // Non-final turn - show interim result but DON'T accumulate
          // (like addRawTranscript: rawText = finalizedRawText + ' ' + text)
          combined = priorFinal ? `${priorFinal} ${incoming}`.trim() : incoming;
        }
      } else {
        // Normal mode: use existing merge logic (backend sends cumulative)
        const priorCorrected = action.payload.isIncremental
          ? (state.correctedText || '').trim()
          : (state.finalizedCorrectedText || '').trim();
        const incomingCorrected = (action.payload.corrected || '').trim();
        const isDelta = incomingCorrected.length > 0
          && incomingCorrected.length < priorCorrected.length
          && priorCorrected.startsWith(incomingCorrected);
        combined = isDelta ? priorCorrected : incomingCorrected;
      }

      if (action.payload.isIncremental) {
        // Incremental: in normal mode backend sends full cumulative corrected text;
        // in deferred mode we accumulate single-turn text above. finalizedCorrectedText
        // is NOT updated here — it captures the definitive final-pass state only.
        state.correctedText = combined;
        state.isIncrementalCorrecting = true;
        if (action.payload.fallback) {
          state.lastCorrectionFailed = true;
        }
      } else {
        // Final pass: full definitive combined correction - NOW safe to update
        state.finalizedCorrectedText = combined;
        state.correctedText = combined;
        state.isIncrementalCorrecting = false;
      }
      // One pending correction has been resolved
      state.pendingCorrections = Math.max(0, state.pendingCorrections - 1);

      // ── Simultaneous typing + dictation merge ──────────────────────────────
      // Only merge dictation if user is editing AND cursor is at the end of text.
      // Dictation that arrives while cursor is not at end is queued and processed
      // later when cursor moves to end, ensuring no data loss.
      if (state.isEditingCorrected && combined) {
        // ``combined`` is the FULL cumulative corrected text (including any prepended prior),
        // not a delta. Extract only the new words by stripping the previously known corrected
        // prefix (captured BEFORE state mutation above).
        const prevCorrected = prevCorrectedSnapshot;
        const newFull = combined;
        let newSegment: string;
        if (newFull.startsWith(prevCorrected) && newFull.length > prevCorrected.length) {
          // Text was appended to previous corrected text
          newSegment = newFull.slice(prevCorrected.length).trim();
        } else if (newFull.length > prevCorrected.length) {
          // LLM re-wrote the text (doesn't start with previous) - treat as full replacement
          newSegment = newFull;
        } else {
          newSegment = '';
        }
        // Use a tolerance window: consider cursor at end if within last 2 chars.
        // For very short text (≤2 chars), require exact end position to prevent
        // allowing appends when cursor is at the very beginning.
        const textLen = state.editedCorrectedText.length;
        const toleranceWindow = textLen <= 2 ? 0 : 2;
        const cursorAtEnd = state.cursorPosition >= textLen - toleranceWindow;
        // Normalize the segment to avoid leading/trailing space issues
        const normalizedSegment = newSegment.trim();
        if (normalizedSegment && cursorAtEnd) {
          // Safe to append - cursor is at (or very near) the end
          const base = state.editedCorrectedText;
          const separator = base.length > 0 && !base.endsWith('\n') && !base.endsWith(' ') ? ' ' : '';
          state.editedCorrectedText = base + separator + normalizedSegment;
          // Update cursor position to reflect the newly appended text
          state.cursorPosition = state.editedCorrectedText.length;
          state.latestDictationDelta = normalizedSegment;
          // Process any queued segments that arrived earlier (deduplicated preserving order)
          // Use ordered deduplication (not Set) to maintain FIFO sequence
          const seen = new Set<string>();
          const uniqueQueue: string[] = [];
          for (const s of state.pendingDictationQueue.map(s => s.trim())) {
            if (!seen.has(s)) {
              seen.add(s);
              uniqueQueue.push(s);
            }
          }
          for (const queued of uniqueQueue) {
            const sep = state.editedCorrectedText.length > 0 &&
                       !state.editedCorrectedText.endsWith('\n') &&
                       !state.editedCorrectedText.endsWith(' ') ? ' ' : '';
            state.editedCorrectedText = state.editedCorrectedText + sep + queued;
          }
          state.pendingDictationQueue = [];
          state.cursorPosition = state.editedCorrectedText.length;
        } else if (normalizedSegment) {
          // Cursor not near end - queue the segment to prevent data loss
          // Avoid duplicates: only queue if not already present
          const trimmedQueue = state.pendingDictationQueue.map(s => s.trim());
          if (!trimmedQueue.includes(normalizedSegment)) {
            // Enforce max queue size to prevent unbounded memory growth (FIFO eviction)
            if (state.pendingDictationQueue.length >= MAX_PENDING_DICTATION_QUEUE) {
              state.pendingDictationQueue.shift(); // Remove oldest
            }
            state.pendingDictationQueue.push(normalizedSegment);
          }
          state.latestDictationDelta = normalizedSegment;
        }
      } else {
        state.latestDictationDelta = '';
      }
    },
    setRefiningInFlight: (state, action: PayloadAction<boolean>) => {
      state.isRefiningInFlight = action.payload;
      // When refining completes after session is final, any unclosed corrections are stale
      if (!action.payload && state.isFinal) {
        state.pendingCorrections = 0;
      }
    },
    updateTranscriptionText: (state, action: PayloadAction<string>) => {
      state.text = action.payload;
    },
    setEditMode: (state, action: PayloadAction<boolean>) => {
      state.isEditMode = action.payload;
    },
    setSessionId: (state, action: PayloadAction<string>) => {
      state.sessionId = action.payload;
    },
    setIsFinal: (state, action: PayloadAction<boolean>) => {
      state.isFinal = action.payload;
      // When session ends with no active refining, clear any stale pending corrections
      if (action.payload && !state.isRefiningInFlight) {
        state.pendingCorrections = 0;
      }
    },
    setClassification: (state, action: PayloadAction<{ category: string; pathologyType: string; confidence: number }>) => {
      state.classification.category = action.payload.category;
      state.classification.pathologyType = action.payload.pathologyType;
      state.classification.confidence = action.payload.confidence;
    },
    clearClassification: (state) => {
      state.classification.category = null;
      state.classification.pathologyType = null;
      state.classification.confidence = null;
    },
    // Append mode: load existing session for continued dictation
    loadSessionForAppend: (state, action: PayloadAction<{
      sessionId: string;
      rawText: string;
      correctedText: string;
      isUserEdited: boolean;
      classification?: { category: string | null; pathologyType: string | null; confidence: number | null };
    }>) => {
      state.sessionId = action.payload.sessionId;
      state.rawText = action.payload.rawText;
      state.finalizedRawText = action.payload.rawText;
      state.text = action.payload.rawText;
      state.finalizedText = action.payload.rawText;
      state.correctedText = action.payload.correctedText;
      state.finalizedCorrectedText = action.payload.correctedText;
      state.isAppendMode = true;
      state.isFinal = true;
      state.isRefiningInFlight = false;
      state.pendingCorrections = 0;
      state.isUserEdited = action.payload.isUserEdited;
      // Clear any stale editing/typing state from a previous session
      state.isEditingCorrected = false;
      state.editedCorrectedText = '';
      state.isManualTypingMode = false;
      state.manualTypedText = '';
      if (action.payload.classification) {
        state.classification = action.payload.classification;
      }
    },
    clearAppendMode: (state) => {
      state.isAppendMode = false;
    },
    // Atomic transition from "loaded session" view into live continue-recording mode.
    // Clears both ``isAppendMode`` and ``isFinal`` in a single reducer so the invariant
    // checked by addCorrectedTranscript (state.isFinal && state.isAppendMode) cannot be
    // briefly true between two separate dispatches — that window could otherwise drop
    // the very first incremental correction of the new dictation segment.
    enterContinueMode: (state) => {
      state.isAppendMode = false;
      state.isFinal = false;
      state.pendingCorrections = 0;
      state.isRefiningInFlight = false;
      // In deferred mode, finalizedCorrectedText accumulates raw text and must be
      // preserved when adding dictation. In normal mode, clear it to prevent
      // duplication since the backend seeds the buffer and returns combined text.
      if (!state.deferredCorrectionEnabled) {
        // Clear the prior-corrected reference so addCorrectedTranscript does not
        // prepend it to the LLM's output. In continue-recording mode the backend
        // seeds the buffer with prior_raw and the LLM corrects the full combined
        // text — the returned corrected value already contains the prior content.
        // Keeping finalizedCorrectedText non-empty causes the startsWith check to
        // fail when the LLM slightly reformats the opening words, triggering the
        // fallback that prepends priorCorrected to the complete output (duplication).
        state.finalizedCorrectedText = '';
      }
    },
    // Corrected transcript editing
    startEditingCorrected: (state) => {
      state.isEditingCorrected = true;
      state.editedCorrectedText = state.correctedText || state.text;
      // Reset cursor to end: the textarea focus+select effect selects all text,
      // so the effective cursor position is at the end of the current content.
      // Without this, the first addCorrectedTranscript during editing would
      // evaluate isAtEnd as (0 >= N) = false and silently drop the delta.
      state.cursorPosition = state.editedCorrectedText.length;
    },
    updateEditedCorrectedText: (state, action: PayloadAction<string>) => {
      state.editedCorrectedText = action.payload;
      state.lastUserEditAt = Date.now();
    },
    cancelEditingCorrected: (state) => {
      state.isEditingCorrected = false;
      state.editedCorrectedText = '';
      state.pendingDictationQueue = [];
      state.latestDictationDelta = '';
      state.cursorPosition = 0;
    },
    // Manual typing mode (type directly without dictation)
    startManualTyping: (state) => {
      state.isManualTypingMode = true;
      state.manualTypedText = state.correctedText || state.text || '';
      // Clear editing state to prevent pollution from previous edit sessions
      state.editedCorrectedText = '';
      state.cursorPosition = 0;
      state.pendingDictationQueue = [];
    },
    updateManualTypedText: (state, action: PayloadAction<string>) => {
      state.manualTypedText = action.payload;
      // Sync to all text fields so pipeline readiness, report generation, and auto-save work
      state.correctedText = action.payload;
      state.finalizedCorrectedText = action.payload;
      state.text = action.payload;
      state.finalizedText = action.payload;
      state.rawText = action.payload;
      state.finalizedRawText = action.payload;
      // Mark as final when text exists so UI controls (Generate Report, etc.) become visible
      if (action.payload && action.payload.length > 0) {
        state.isFinal = true;
      }
    },
    saveManualTypedText: (state) => {
      state.text = state.manualTypedText;
      state.finalizedText = state.manualTypedText;
      state.rawText = state.manualTypedText;
      state.finalizedRawText = state.manualTypedText;
      state.correctedText = state.manualTypedText;
      state.finalizedCorrectedText = state.manualTypedText;
      state.isManualTypingMode = false;
      state.isUserEdited = true;
      state.isFinal = true;
    },
    cancelManualTyping: (state) => {
      state.isManualTypingMode = false;
      state.manualTypedText = '';
      // Clear any queued dictation to prevent stale data in future sessions
      state.pendingDictationQueue = [];
      state.latestDictationDelta = '';
    },
    saveEditedCorrectedText: (state) => {
      state.correctedText = state.editedCorrectedText;
      state.finalizedCorrectedText = state.editedCorrectedText;
      // If there is no raw finalizedText (manual typing path), mirror the edited text
      // so pipeline readiness selectors that check finalizedText.length > 0 pass correctly
      if (!state.finalizedText) {
        state.finalizedText = state.editedCorrectedText;
        state.text = state.editedCorrectedText;
      }
      state.isEditingCorrected = false;
      state.isUserEdited = true;
      state.lastUserEditAt = Date.now();
      // NOTE: pendingDictationQueue is NOT cleared here - it's cleared after
      // successful API persistence in TranscriptPanel.handleSaveEdit to prevent
      // data loss on save failure.
      state.latestDictationDelta = '';
      state.cursorPosition = 0;
    },
    // Like saveEditedCorrectedText but keeps isEditingCorrected = true so the
    // textarea stays mounted during autosave. The user can keep typing without
    // losing cursor position.
    commitEditedCorrectedText: (state) => {
      state.correctedText = state.editedCorrectedText;
      state.finalizedCorrectedText = state.editedCorrectedText;
      if (!state.finalizedText) {
        state.finalizedText = state.editedCorrectedText;
        state.text = state.editedCorrectedText;
      }
      state.isUserEdited = true;
      state.lastUserEditAt = Date.now();
      state.latestDictationDelta = '';
    },
    setCursorPosition: (state, action: PayloadAction<number>) => {
      state.cursorPosition = action.payload;
      // If cursor moved to end and we have pending dictation, process the queue.
      // For very short text (≤2 chars), require exact end position.
      const textLen = state.editedCorrectedText.length;
      const toleranceWindow = textLen <= 2 ? 0 : 2;
      const cursorAtEnd = action.payload >= textLen - toleranceWindow;
      if (cursorAtEnd && state.pendingDictationQueue.length > 0 && state.isEditingCorrected) {
        // Deduplicate preserving FIFO order (don't use Set which loses ordering)
        const seen = new Set<string>();
        const uniqueQueue: string[] = [];
        for (const s of state.pendingDictationQueue.map(s => s.trim())) {
          if (!seen.has(s)) {
            seen.add(s);
            uniqueQueue.push(s);
          }
        }
        for (const queued of uniqueQueue) {
          const sep = state.editedCorrectedText.length > 0 &&
                     !state.editedCorrectedText.endsWith('\n') &&
                     !state.editedCorrectedText.endsWith(' ') ? ' ' : '';
          state.editedCorrectedText = state.editedCorrectedText + sep + queued;
        }
        state.pendingDictationQueue = [];
        state.cursorPosition = state.editedCorrectedText.length;
        state.latestDictationDelta = ''; // Clear delta after processing
      }
    },
    resetTranscription: (state) => {
      state.text = '';
      state.finalizedText = '';
      state.rawText = '';
      state.finalizedRawText = '';
      state.correctedText = '';
      state.finalizedCorrectedText = '';
      state.pendingCorrections = 0;
      state.isEditMode = false;
      state.isFinal = false;
      state.sessionId = null;
      state.error = null;
      state.isIncrementalCorrecting = false;
      state.isRefiningInFlight = false;
      state.isAppendMode = false;
      state.isEditingCorrected = false;
      state.editedCorrectedText = '';
      state.isUserEdited = false;
      state.cursorPosition = 0;
      state.latestDictationDelta = '';
      state.pendingDictationQueue = [];
      state.lastUserEditAt = 0;
      state.deferredPatches = 0;
      state.appliedPatchIds = [];
      state.lastCorrectionFailed = false;
      state.isManualTypingMode = false;
      state.manualTypedText = '';
      state.classification = { category: null, pathologyType: null, confidence: null };
    },
    clearError: (state) => {
      state.error = null;
    },
    clearDictationDelta: (state) => {
      state.latestDictationDelta = '';
    },
    clearPendingDictationQueue: (state) => {
      state.pendingDictationQueue = [];
    },
    setDeferredCorrectionEnabled: (state, action: PayloadAction<boolean>) => {
      state.deferredCorrectionEnabled = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(saveTranscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveTranscription.fulfilled, (state, action) => {
        state.loading = false;
        state.history.unshift(action.payload);
        // Persist the session ID so report generation can proceed without a manual save step
        if (action.payload.sessionId && !state.sessionId) {
          state.sessionId = action.payload.sessionId;
        }
      })
      .addCase(saveTranscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchTranscriptionHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTranscriptionHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.history = action.payload;
      })
      .addCase(fetchTranscriptionHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  addTranscriptionText,
  addRawTranscript,
  addCorrectedTranscript,
  addDeferredRawToCorrected,
  applyCorrectionPatch,
  acceptDeferredPatches,
  setCorrectionFailed,
  updateTranscriptionText,
  setEditMode,
  setFinalRawText,
  setSessionId,
  setIsFinal,
  setClassification,
  setRefiningInFlight,
  clearClassification,
  loadSessionForAppend,
  clearAppendMode,
  enterContinueMode,
  startEditingCorrected,
  updateEditedCorrectedText,
  cancelEditingCorrected,
  saveEditedCorrectedText,
  commitEditedCorrectedText,
  setCursorPosition,
  resetTranscription,
  clearError,
  clearDictationDelta,
  clearPendingDictationQueue,
  setDeferredCorrectionEnabled,
  startManualTyping,
  updateManualTypedText,
  saveManualTypedText,
  cancelManualTyping,
} = transcriptionSlice.actions;

export default transcriptionSlice.reducer;